import { Entity, GltfExporter } from 'playcanvas';

import { Element, ElementType } from './element';
import { Scene } from './scene';

interface GlbExportOptions {
    maxTextureSize?: number;
}

// the Model/SceneObject/Water subset shared by every ElementType.model element: an
// Element (so it can be re-attached through the hierarchy) that owns an entity
type MeshElement = Element & { entity: Entity };

// Gathers every model-type scene element (imported models, primitive shapes, water -
// never splats) under a detached scratch root and exports it via PlayCanvas's built-in
// GltfExporter. Reparenting is temporary and always undone in `finally`, so this never
// leaves the live scene graph modified, regardless of success or failure.
const buildGlbExport = async (scene: Scene, options: GlbExportOptions = {}): Promise<ArrayBuffer> => {
    const elements = scene.getElementsByType(ElementType.model) as MeshElement[];

    if (elements.length === 0) {
        throw new Error('Nothing to export: scene has no model content');
    }

    const scratchRoot = new Entity('glbExportScratch');
    elements.forEach(element => scratchRoot.addChild(element.entity));

    try {
        const exporter = new GltfExporter();
        return await exporter.build(scratchRoot, options);
    } finally {
        // restore through attachEntity, not contentRoot: a grouped element must go back
        // to its group, and reparenting it to the root here would silently flatten the
        // scene hierarchy on every export
        elements.forEach(element => scene.attachEntity(element));
        scratchRoot.destroy();
    }
};

export { buildGlbExport, GlbExportOptions };
